import {
  filterServicesByOrderId,
  filterServicesByOrderNumber,
  sameOrderNumberValue,
  sameServiceId
} from 'src/entities/file/lib/service-table.utils';
import {
  ConfirmPreliminaryOrderResult,
  CreateOrderInput,
  CreatedOrder
} from '../model/create-order.model';
import {
  OrderPdfStatus,
  OrderReservationStatus,
  OrderStatus
} from '../model/order-status.model';
import { UpdateOrderResult } from '../model/update-order.model';

export type ApplyCreatedOrdersDefaults = Pick<
  CreateOrderInput,
  'reservationNumber' | 'orderObservation' | 'scheduledPaymentDate' | 'commission'
>;

/** Formato YYYY-MM-DD aceptado por updateOrderScheduledPaymentDate. */
export function formatScheduledPaymentDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Fecha calendario (sin hora) para enviar al crear OS como ISO UTC medianoche. */
export function toScheduledPaymentDateIso(date: Date): string {
  return `${formatScheduledPaymentDate(date)}T00:00:00.000Z`;
}

/**
 * Interpreta scheduledPaymentDate como fecha calendario (Perú),
 * sin desplazar un día por zona horaria.
 */
export function parseScheduledPaymentDateForInput(
  value: string | null | undefined
): Date | null {
  if (!value?.trim()) {
    return null;
  }

  const trimmed = value.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  if (trimmed.includes('T')) {
    const [year, month, day] = trimmed.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatScheduledPaymentDateDisplay(
  value: string | null | undefined
): string {
  if (!value?.trim()) {
    return '-';
  }

  const date = parseScheduledPaymentDateForInput(value);
  if (!date) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Recalcula estado del padre TICKET a partir de hijos ya pintados. */
export function resolveEstadoServicioTicketsFromChildren(
  tickets: any[] | null | undefined
): {
  hasOrderedTickets: boolean;
  estadoServicioTickets: 'CREADO' | 'EN_PROCESO';
} {
  const list = Array.isArray(tickets) ? tickets : [];
  const hasOrdered = list.some((ticket) => Boolean(ticket?.order?.orderId));
  const hasCreadoLibre = list.some((ticket) => {
    const raw = String(ticket?.estadoTicket ?? '')
      .toUpperCase()
      .replace(/\s+/g, '_');
    const estado = raw === 'CREATED' ? 'CREADO' : raw;
    return !ticket?.order?.orderId && estado === 'CREADO';
  });

  if (hasCreadoLibre && hasOrdered) {
    return { hasOrderedTickets: true, estadoServicioTickets: 'EN_PROCESO' };
  }
  if (hasCreadoLibre) {
    return { hasOrderedTickets: false, estadoServicioTickets: 'CREADO' };
  }
  if (hasOrdered) {
    return { hasOrderedTickets: true, estadoServicioTickets: 'EN_PROCESO' };
  }
  return { hasOrderedTickets: false, estadoServicioTickets: 'CREADO' };
}

/**
 * Asocia createdOrders a filas ticket hijas.
 * 1 OS puede incluir N tickets (mismo o distintos padres) → pintar TODOS los ticketIds.
 */
export function applyCreatedOrdersToTicketRows(
  tickets: any[] | null | undefined,
  createdOrders: CreatedOrder[] | null | undefined,
  defaults?: ApplyCreatedOrdersDefaults | null
): any[] {
  if (!Array.isArray(tickets) || !tickets.length || !createdOrders?.length) {
    return tickets ?? [];
  }

  const byTicketId = new Map<string, CreatedOrder>();
  for (const created of createdOrders) {
    for (const ticketId of created?.ticketIds ?? []) {
      const key = String(ticketId || '').trim();
      if (key) {
        byTicketId.set(key, created);
      }
    }
  }

  if (!byTicketId.size) return tickets;

  return tickets.map((ticket) => {
    const created = byTicketId.get(String(ticket?.id));
    if (!created) return ticket;
    return {
      ...ticket,
      order: {
        orderId: created.orderId,
        orderNumber: created.orderNumber,
        displayCode:
          (created.orderStatus ?? OrderStatus.CONFIRMADO) ===
          OrderStatus.PRELIMINAR
            ? `P-${created.orderNumber}`
            : created.orderNumber,
        orderStatus: created.orderStatus ?? OrderStatus.CONFIRMADO,
        reservationNumber:
          created.reservationNumber ?? defaults?.reservationNumber ?? null,
        reservationStatus: OrderReservationStatus.CONFIRMADO,
        orderObservation:
          created.orderObservation ?? defaults?.orderObservation ?? null,
        scheduledPaymentDate:
          created.scheduledPaymentDate ??
          defaults?.scheduledPaymentDate ??
          null,
        freezeDeadlineAt: created.freezeDeadlineAt ?? null,
        pdfStatus: created.pdfStatus ?? OrderPdfStatus.PENDING,
        pdfGeneratedAt: null,
        lastPdfError: null,
        commissionAmount: created.commissionAmount ?? null,
        commissionPercent: created.commissionPercent ?? null,
        // Multi-padre: para refrescar todos al eliminar OS.
        serviceIds: created.serviceIds ?? []
      },
      hasOrder: true,
      orderIssuanceFrozen:
        (created.orderStatus ?? OrderStatus.CONFIRMADO) !==
        OrderStatus.PRELIMINAR,
      estadoTicket:
        (created.orderStatus ?? OrderStatus.CONFIRMADO) ===
        OrderStatus.PRELIMINAR
          ? ticket.estadoTicket || 'CREADO'
          : 'CONFIRMADO',
      checked: false
    };
  });
}

function collectCreatedOrderTicketIds(
  createdOrders: CreatedOrder[]
): Set<string> {
  const ids = new Set<string>();
  for (const created of createdOrders) {
    for (const ticketId of created?.ticketIds ?? []) {
      const key = String(ticketId || '').trim();
      if (key) ids.add(key);
    }
  }
  return ids;
}

function serviceHasTicketInSet(service: any, ticketIds: Set<string>): boolean {
  if (!ticketIds.size || !Array.isArray(service?.tickets)) return false;
  return service.tickets.some((ticket: any) =>
    ticketIds.has(String(ticket?.id || '').trim())
  );
}

/**
 * Aplica las OS creadas por el backend sobre las filas locales de servicios.
 * Multi-padre (1 OS / N líneas): serviceIds + ticketIds pueden abarcar varios padres.
 */
export function applyCreatedOrdersToServices(
  services: any[],
  createdOrders: CreatedOrder[] | null | undefined,
  idKey: string = 'id',
  defaults?: ApplyCreatedOrdersDefaults | null
): void {
  if (!Array.isArray(services) || !Array.isArray(createdOrders)) return;
  if (!createdOrders.length) return;

  const serviceIdsTouched = new Set<string>();
  for (const created of createdOrders) {
    for (const serviceId of created?.serviceIds ?? []) {
      serviceIdsTouched.add(String(serviceId));
    }
  }
  const createdTicketIds = collectCreatedOrderTicketIds(createdOrders);

  for (const servicio of services) {
    const serviceKey = String(servicio?.[idKey] ?? '').trim();
    const touchedByServiceId =
      Boolean(serviceKey) &&
      [...serviceIdsTouched].some((id) => sameServiceId(servicio, id, idKey));
    const isTicketParent =
      String(servicio?.tipoServicio ?? '').toUpperCase() === 'TICKET';
    const touchedByTicket =
      isTicketParent && serviceHasTicketInSet(servicio, createdTicketIds);

    if (!touchedByServiceId && !touchedByTicket) {
      continue;
    }

    if (isTicketParent) {
      const relatedOrders = createdOrders.filter((created) => {
        const inServiceIds = (created.serviceIds || []).some((id) =>
          sameServiceId(servicio, String(id), idKey)
        );
        if (inServiceIds) return true;
        return (created.ticketIds || []).some((ticketId) =>
          (servicio.tickets || []).some(
            (ticket: any) => String(ticket?.id) === String(ticketId)
          )
        );
      });
      const canPaintTickets = relatedOrders.some(
        (created) => (created.ticketIds || []).length > 0
      );

      if (
        canPaintTickets &&
        Array.isArray(servicio.tickets) &&
        servicio.tickets.length
      ) {
        servicio.tickets = applyCreatedOrdersToTicketRows(
          servicio.tickets,
          relatedOrders,
          defaults
        );
        applyCommissionToTicketRows(
          servicio,
          relatedOrders,
          defaults?.commission
        );
        servicio.ticketsLoaded = true;
        const nextState = resolveEstadoServicioTicketsFromChildren(
          servicio.tickets
        );
        servicio.hasOrderedTickets = nextState.hasOrderedTickets;
        servicio.estadoServicioTickets = nextState.estadoServicioTickets;
        if (relatedOrders.length) {
          servicio.orderIssuanceFrozen = !relatedOrders.some(
            (order) => order.orderStatus === OrderStatus.PRELIMINAR
          );
        }
        // Padre TICKET: no estampar OS residual si aún hay hijos libres.
        if (
          !nextState.hasOrderedTickets ||
          nextState.estadoServicioTickets === 'CREADO' ||
          servicio.tickets.some((t: any) => {
            const raw = String(t?.estadoTicket ?? '')
              .toUpperCase()
              .replace(/\s+/g, '_');
            const estado = raw === 'CREATED' ? 'CREADO' : raw;
            return !t?.order?.orderId && estado === 'CREADO';
          })
        ) {
          servicio.order = null;
        }
      } else {
        // Fan-out sin ticketIds / tickets no cargados → marcar padre y refetch expand.
        servicio.hasOrderedTickets = true;
        servicio.estadoServicioTickets = 'EN_PROCESO';
        if (relatedOrders.length) {
          servicio.orderIssuanceFrozen = !relatedOrders.some(
            (order) => order.orderStatus === OrderStatus.PRELIMINAR
          );
        }
        servicio.ticketsLoaded = false;
        servicio.tickets = undefined;
      }
      applyCommissionToClosedTicketParent(
        servicio,
        relatedOrders,
        defaults?.commission
      );
      servicio.checked = false;
      servicio.selected = false;
      continue;
    }

    const created = createdOrders.find((c) =>
      (c.serviceIds || []).some((id) => sameServiceId(servicio, String(id), idKey))
    );
    if (!created) continue;

    servicio.order = {
      orderId: created.orderId,
      orderNumber: created.orderNumber,
      displayCode:
        (created.orderStatus ?? OrderStatus.CONFIRMADO) ===
        OrderStatus.PRELIMINAR
          ? `P-${created.orderNumber}`
          : created.orderNumber,
      orderStatus: created.orderStatus ?? OrderStatus.CONFIRMADO,
      reservationNumber:
        created.reservationNumber ?? defaults?.reservationNumber ?? null,
      reservationStatus: OrderReservationStatus.CONFIRMADO,
      orderObservation:
        created.orderObservation ?? defaults?.orderObservation ?? null,
      scheduledPaymentDate:
        created.scheduledPaymentDate ??
        defaults?.scheduledPaymentDate ??
        null,
      freezeDeadlineAt: created.freezeDeadlineAt ?? null,
      pdfStatus: created.pdfStatus ?? OrderPdfStatus.PENDING,
      pdfGeneratedAt: null,
      lastPdfError: null,
      commissionAmount: created.commissionAmount ?? null,
      commissionPercent: created.commissionPercent ?? null
    };
    servicio.orderIssuanceFrozen =
      (created.orderStatus ?? OrderStatus.CONFIRMADO) !==
      OrderStatus.PRELIMINAR;
    applyCommissionToServiceRow(servicio, created, defaults?.commission);
    servicio.checked = false;
  }
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function isDollarRow(entity: any): boolean {
  const raw = String(
    entity?.tipoMoneda ??
      entity?.moneda ??
      entity?.objTarifa?.monedaTarifa ??
      ''
  ).toUpperCase();
  return raw === 'D' || raw === 'DOLAR' || raw === 'USD' || raw === '$';
}

function sideAmount(entity: any, currency: 'D' | 'S'): number | null {
  const edited = entity?.editOperation
    ? currency === 'D'
      ? entity.newTotalCostoDolares
      : entity.newTotalCostoSoles
    : null;
  const original =
    currency === 'D' ? entity?.totalCostoDolares : entity?.totalCostoSoles;
  const amount = entity?.editOperation ? (edited ?? original) : original;
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? roundMoney(parsed) : null;
}

function displayedBase(entity: any): number | null {
  const side = sideAmount(entity, isDollarRow(entity) ? 'D' : 'S');
  if (side != null) {
    return side;
  }
  const total = Number(entity?.total);
  return Number.isFinite(total) ? roundMoney(total) : null;
}

/** Deja en la fila el monto a pagar (el que la grilla lee como `total`). */
function writePayable(entity: any, base: number, payable: number): void {
  const dollar = isDollarRow(entity);
  const baseDolares = sideAmount(entity, 'D');
  const baseSoles = sideAmount(entity, 'S');
  const tipoCambio = Number(entity?.tipoCambio);
  const rate =
    Number.isFinite(tipoCambio) && tipoCambio > 0 ? tipoCambio : null;
  const dolares = dollar
    ? payable
    : base > 0 && baseDolares != null
      ? roundMoney((baseDolares * payable) / base)
      : rate != null
        ? roundMoney(payable / rate)
        : null;
  const soles = dollar
    ? base > 0 && baseSoles != null
      ? roundMoney((baseSoles * payable) / base)
      : rate != null
        ? roundMoney(payable * rate)
        : null
    : payable;
  entity.editOperation = true;
  if (dolares != null) {
    entity.newTotalCostoDolares = dolares;
  }
  if (soles != null) {
    entity.newTotalCostoSoles = soles;
  }
  entity.total = payable;
  entity.monto = payable;
  entity.costoUnitario = null;
  entity.commissionCostApplied = true;
  entity.commissionCostDelta = roundMoney(payable - base);
}

function payableFor(
  base: number,
  commission: NonNullable<ApplyCreatedOrdersDefaults['commission']>
): number | null {
  const value = roundMoney(Number(commission.value));
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  const extra =
    commission.type === 'PERCENTAGE' ? roundMoney((base * value) / 100) : value;
  const payable = roundMoney(base + extra);
  return payable === base ? null : payable;
}

function orderHasCommission(order: CreatedOrder): boolean {
  return Number(order?.commissionAmount) > 0;
}

function applyCommissionToServiceRow(
  service: any,
  order: CreatedOrder,
  commission: ApplyCreatedOrdersDefaults['commission']
): void {
  if (!commission || !orderHasCommission(order)) {
    return;
  }
  const base = displayedBase(service);
  if (base == null) {
    return;
  }
  const payable = payableFor(base, commission);
  if (payable == null) {
    return;
  }
  writePayable(service, base, payable);
}

function applyCommissionToTicketRows(
  parent: any,
  orders: CreatedOrder[],
  commission: ApplyCreatedOrdersDefaults['commission']
): void {
  if (!commission || !Array.isArray(parent?.tickets)) {
    return;
  }
  const ticketIds = new Set<string>();
  for (const order of orders) {
    if (!orderHasCommission(order)) {
      continue;
    }
    for (const ticketId of order.ticketIds ?? []) {
      const key = String(ticketId || '').trim();
      if (key) {
        ticketIds.add(key);
      }
    }
  }
  if (!ticketIds.size) {
    return;
  }
  let changed = false;
  for (const ticket of parent.tickets) {
    if (!ticketIds.has(String(ticket?.id || '').trim())) {
      continue;
    }
    const base = displayedBase(ticket);
    if (base == null) {
      continue;
    }
    const payable = payableFor(base, commission);
    if (payable == null) {
      continue;
    }
    writePayable(ticket, base, payable);
    changed = true;
  }
  if (changed) {
    writeParentFromTickets(parent);
  }
}

function applyCommissionToClosedTicketParent(
  parent: any,
  orders: CreatedOrder[],
  commission: ApplyCreatedOrdersDefaults['commission']
): void {
  if (!commission || (Array.isArray(parent?.tickets) && parent.tickets.length)) {
    return;
  }
  const commissioned = orders.filter(orderHasCommission);
  if (!commissioned.length) {
    return;
  }
  const base = displayedBase(parent);
  if (base == null) {
    return;
  }
  if (commission.type === 'PERCENTAGE') {
    const payable = payableFor(base, commission);
    if (payable != null) {
      writePayable(parent, base, payable);
    }
    return;
  }
  const ticketCount = commissioned.reduce(
    (sum, order) => sum + (order.ticketIds?.length ?? 0),
    0
  );
  if (ticketCount <= 0) {
    return;
  }
  const value = roundMoney(Number(commission.value));
  const payable = roundMoney(base + value * ticketCount);
  if (payable !== base) {
    writePayable(parent, base, payable);
  }
}

function writeParentFromTickets(parent: any): void {
  const previous = displayedBase(parent);
  let dolares = 0;
  let soles = 0;
  for (const ticket of parent.tickets ?? []) {
    if (ticket?.isDeleted) {
      continue;
    }
    dolares += moneyOf(ticket, 'D');
    soles += moneyOf(ticket, 'S');
  }
  parent.editOperation = true;
  parent.newTotalCostoDolares = roundMoney(dolares);
  parent.newTotalCostoSoles = roundMoney(soles);
  parent.total = isDollarRow(parent)
    ? parent.newTotalCostoDolares
    : parent.newTotalCostoSoles;
  parent.monto = parent.total;
  parent.costoUnitario = null;
  parent.commissionCostApplied = true;
  parent.commissionCostDelta =
    previous != null ? roundMoney(parent.total - previous) : null;
}

/** Aplica el resultado de una mutation de Orders sobre filas con el mismo orderNumber. */
export function applyOrderUpdateToServices(
  services: any[],
  orderNumber: string | number | null | undefined,
  update: Partial<UpdateOrderResult> | null | undefined,
  fallbackService?: any
): void {
  if (!update) {
    return;
  }

  let targets = orderNumber
    ? filterServicesByOrderNumber(services, orderNumber)
    : [];

  if (!targets.length && update.orderId) {
    targets = filterServicesByOrderId(services, update.orderId);
  }

  if (!targets.length && fallbackService) {
    targets = [fallbackService];
  }

  targets.forEach((item) => {
    item.order = { ...(item.order ?? {}), ...update };
  });
}

/**
 * Al confirmar una OS preliminar se actualizan TODAS las filas de esa OS:
 * servicios sueltos y tickets hijos de cualquier padre, no solo la fila
 * desde donde se confirmó.
 */
export function applyConfirmedPreliminaryOrder(
  services: any[],
  result: ConfirmPreliminaryOrderResult | null | undefined
): void {
  if (!result) {
    return;
  }

  const matches = (order: any): boolean =>
    Boolean(order) &&
    ((Boolean(result.orderId) &&
      order.orderId != null &&
      String(order.orderId) === String(result.orderId)) ||
      sameOrderNumberValue(order.orderNumber, result.orderNumber));

  const patch = (order: any): void => {
    order.orderStatus = result.orderStatus;
    if (result.displayCode) {
      order.displayCode = result.displayCode;
    }
    order.pdfStatus = result.pdfStatus;
  };

  for (const item of services ?? []) {
    const parentMatches = matches(item?.order);
    if (parentMatches) {
      patch(item.order);
      item.orderIssuanceFrozen = true;
      item.orderStatus = result.orderStatus;
      if ('estadoTicket' in item) {
        item.estadoTicket = 'CONFIRMADO';
      }
    }

    if (!Array.isArray(item?.tickets)) {
      continue;
    }

    let ticketsTouched = false;
    for (const ticket of item.tickets) {
      if (!matches(ticket?.order)) {
        continue;
      }
      patch(ticket.order);
      ticket.estadoTicket = 'CONFIRMADO';
      ticket.hasOrder = true;
      ticketsTouched = true;
    }

    if (ticketsTouched && !parentMatches) {
      // El padre sigue editable si otro ticket conserva una OS preliminar.
      item.orderIssuanceFrozen = !item.tickets.some(
        (ticket: any) => ticket?.order?.orderStatus === 'PRELIMINAR'
      );
    }
  }
}

/**
 * Al editar «Confirmar antes del» se actualiza la fecha en TODAS las filas
 * de esa OS: servicios sueltos y tickets hijos de cualquier padre.
 */
export function applyFreezeDeadlineToServices(
  services: any[],
  orderRef: {
    orderId?: string | null;
    orderNumber?: string | number | null;
  } | null | undefined,
  freezeDeadlineAt: string | null | undefined
): void {
  if (!orderRef || !freezeDeadlineAt) {
    return;
  }

  const matches = (order: any): boolean =>
    Boolean(order) &&
    ((Boolean(orderRef.orderId) &&
      order.orderId != null &&
      String(order.orderId) === String(orderRef.orderId)) ||
      sameOrderNumberValue(order.orderNumber, orderRef.orderNumber));

  for (const item of services ?? []) {
    if (matches(item?.order)) {
      item.order.freezeDeadlineAt = freezeDeadlineAt;
      item.freezeDeadlineAt = freezeDeadlineAt;
    }
    if (!Array.isArray(item?.tickets)) {
      continue;
    }
    for (const ticket of item.tickets) {
      if (matches(ticket?.order)) {
        ticket.order.freezeDeadlineAt = freezeDeadlineAt;
      }
    }
  }
}

function orderHadCommission(order: any): boolean {
  const amount = Number(order?.commissionAmount);
  return Number.isFinite(amount) && amount !== 0;
}

function shouldRestoreCommissionCosts(entity: any, order: any): boolean {
  return entity?.commissionCostApplied === true || orderHadCommission(order);
}

function moneyOf(entity: any, currency: 'D' | 'S'): number {
  const side = sideAmount(entity, currency);
  if (side != null) {
    return side;
  }
  if ((currency === 'D') === isDollarRow(entity)) {
    const total = Number(entity?.total);
    if (Number.isFinite(total)) {
      return total;
    }
  }
  return 0;
}

/** Quita el costo de comisión y deja el monto inicial que pinta la grilla. */
function restoreCommissionCosts(
  entity: any,
  order: any,
  lineCount = 1
): boolean {
  if (!entity || !shouldRestoreCommissionCosts(entity, order)) {
    return false;
  }

  const initial = Number(entity.costoInicial);
  const original = Number(
    isDollarRow(entity) ? entity.totalCostoDolares : entity.totalCostoSoles
  );
  const delta = Number(entity.commissionCostDelta);
  const current = Number(entity.total);
  const percent = Number(order?.commissionPercent);
  const commission = Number(order?.commissionAmount);
  let restored: number | null = null;
  if (Number.isFinite(initial)) {
    restored = roundMoney(initial);
  } else if (Number.isFinite(original)) {
    restored = roundMoney(original);
  } else if (Number.isFinite(delta) && delta !== 0 && Number.isFinite(current)) {
    restored = roundMoney(current - delta);
  } else if (
    Number.isFinite(percent) &&
    percent > 0 &&
    Number.isFinite(current)
  ) {
    restored = roundMoney(current / (1 + percent / 100));
  } else if (
    Number.isFinite(commission) &&
    commission !== 0 &&
    Number.isFinite(current)
  ) {
    const share = commission / Math.max(lineCount, 1);
    restored = roundMoney(current - share);
  }

  entity.editOperation = false;
  entity.newTotalCostoDolares = null;
  entity.newTotalCostoSoles = null;
  entity.commissionCostApplied = false;
  entity.commissionCostDelta = null;
  if (restored != null) {
    entity.total = restored;
    entity.monto = restored;
  }
  return true;
}

function recomputeParentAfterTicketRelease(parent: any): void {
  const tickets = Array.isArray(parent?.tickets) ? parent.tickets : [];
  let dolares = 0;
  let soles = 0;
  let anyEdited = false;
  for (const ticket of tickets) {
    if (ticket?.isDeleted) {
      continue;
    }
    if (ticket?.editOperation === true) {
      anyEdited = true;
    }
    dolares += moneyOf(ticket, 'D');
    soles += moneyOf(ticket, 'S');
  }
  const total = isDollarRow(parent) ? roundMoney(dolares) : roundMoney(soles);
  parent.commissionCostApplied = false;
  parent.commissionCostDelta = null;
  parent.total = total;
  parent.monto = total;
  if (anyEdited) {
    parent.editOperation = true;
    parent.newTotalCostoDolares = roundMoney(dolares);
    parent.newTotalCostoSoles = roundMoney(soles);
    return;
  }
  parent.editOperation = false;
  parent.newTotalCostoDolares = null;
  parent.newTotalCostoSoles = null;
}

/** null, no false: false es OS preliminar todavía abierta. */
function releasedOrderFields(): Record<string, null> {
  return {
    order: null,
    orderIssuanceFrozen: null,
    orderId: null,
    numOS: null,
    orderLineId: null,
    idOS: null
  };
}

function matchesReleasedOrder(
  order: any,
  orderNumber: string | number | null | undefined,
  orderId?: string | null
): boolean {
  if (orderId && order?.orderId && String(order.orderId) === String(orderId)) {
    return true;
  }
  return sameOrderNumberValue(order?.orderNumber, orderNumber);
}

/** Tras deleteOrder: libera padre y tickets hijos con esa OS (multi-línea). */
export function clearServiceOrdersByOrderNumber(
  services: any[],
  orderNumber: string | number | null | undefined,
  orderId?: string | null
): void {
  if (!orderNumber && !orderId) return;

  let lineCount = 0;
  for (const item of services ?? []) {
    if (matchesReleasedOrder(item?.order, orderNumber, orderId)) {
      lineCount += 1;
    }
    if (Array.isArray(item?.tickets)) {
      for (const ticket of item.tickets) {
        if (matchesReleasedOrder(ticket?.order, orderNumber, orderId)) {
          lineCount += 1;
        }
      }
    }
  }
  if (lineCount < 1) lineCount = 1;

  for (const item of services ?? []) {
    let touchedTickets = false;
    let restoredTicket = false;

    if (matchesReleasedOrder(item?.order, orderNumber, orderId)) {
      restoreCommissionCosts(item, item.order, lineCount);
      Object.assign(item, releasedOrderFields());
    }

    if (Array.isArray(item?.tickets) && item.tickets.length) {
      item.tickets = item.tickets.map((ticket: any) => {
        if (!matchesReleasedOrder(ticket?.order, orderNumber, orderId)) {
          return ticket;
        }
        touchedTickets = true;
        if (restoreCommissionCosts(ticket, ticket.order, lineCount)) {
          restoredTicket = true;
        }
        return {
          ...ticket,
          ...releasedOrderFields(),
          hasOrder: false,
          estadoTicket: 'CREADO',
          checked: false
        };
      });
    }

    if (restoredTicket) {
      recomputeParentAfterTicketRelease(item);
    }

    if (touchedTickets) {
      item.ticketsLoaded = true;
      const nextState = resolveEstadoServicioTicketsFromChildren(item.tickets);
      item.hasOrderedTickets = nextState.hasOrderedTickets;
      item.estadoServicioTickets = nextState.estadoServicioTickets;
      const stillOrdered = item.tickets.filter(
        (ticket: any) => ticket?.order?.orderId
      );
      if (!stillOrdered.length || !nextState.hasOrderedTickets) {
        Object.assign(item, releasedOrderFields());
      } else {
        item.orderIssuanceFrozen = stillOrdered.some(
          (ticket: any) => ticket?.orderIssuanceFrozen !== false
        );
      }
      if (
        !nextState.hasOrderedTickets ||
        nextState.estadoServicioTickets === 'CREADO' ||
        item.tickets.some((t: any) => {
          const raw = String(t?.estadoTicket ?? '')
            .toUpperCase()
            .replace(/\s+/g, '_');
          const estado = raw === 'CREATED' ? 'CREADO' : raw;
          return !t?.order?.orderId && estado === 'CREADO';
        })
      ) {
        item.order = null;
      }
    }
  }
}

type ReleasedCost = {
  serviceId?: string | null;
  ticketId?: string | null;
  editOperation?: boolean | null;
  newTotalCostoDolares?: number | null;
  newTotalCostoSoles?: number | null;
  totalCostoDolares?: number | null;
  totalCostoSoles?: number | null;
};

function releasedDisplay(entity: any, cost: ReleasedCost): number {
  const dollar = isDollarRow(entity);
  const edited = Number(
    dollar ? cost.newTotalCostoDolares : cost.newTotalCostoSoles
  );
  const initial = Number(dollar ? cost.totalCostoDolares : cost.totalCostoSoles);
  if (cost.editOperation && Number.isFinite(edited)) {
    return roundMoney(edited);
  }
  if (Number.isFinite(initial)) {
    return roundMoney(initial);
  }
  const current = Number(entity?.total);
  return Number.isFinite(current) ? roundMoney(current) : 0;
}

function releasedCostPatch(entity: any, cost: ReleasedCost): Record<string, unknown> {
  const dollar = isDollarRow(entity);
  const initial = Number(dollar ? cost.totalCostoDolares : cost.totalCostoSoles);
  const total = releasedDisplay(entity, cost);
  return {
    editOperation: cost.editOperation === true,
    newTotalCostoDolares:
      cost.editOperation === true ? (cost.newTotalCostoDolares ?? null) : null,
    newTotalCostoSoles:
      cost.editOperation === true ? (cost.newTotalCostoSoles ?? null) : null,
    totalCostoDolares: cost.totalCostoDolares ?? null,
    totalCostoSoles: cost.totalCostoSoles ?? null,
    costoInicial: Number.isFinite(initial)
      ? roundMoney(initial)
      : (entity?.costoInicial ?? null),
    commissionCostApplied: false,
    commissionCostDelta: null,
    total,
    monto: total
  };
}

function applyReleasedCosts(
  services: any[],
  costs: ReleasedCost[] | null | undefined
): void {
  if (!costs?.length) {
    return;
  }
  const byTicket = new Map<string, ReleasedCost>();
  const byService = new Map<string, ReleasedCost>();
  for (const cost of costs) {
    if (cost.ticketId) byTicket.set(String(cost.ticketId), cost);
    else if (cost.serviceId) byService.set(String(cost.serviceId), cost);
  }

  for (const item of services ?? []) {
    const serviceCost =
      byService.get(String(item?.id ?? '')) ??
      byService.get(String(item?.serviceId ?? ''));
    if (serviceCost) {
      Object.assign(item, releasedCostPatch(item, serviceCost));
    }
    if (!Array.isArray(item?.tickets)) {
      continue;
    }
    for (const ticket of item.tickets) {
      const cost = byTicket.get(String(ticket?.id ?? ''));
      if (cost) {
        Object.assign(ticket, releasedCostPatch(ticket, cost));
      }
    }
  }
}

export interface DeleteOrderDialogResult {
  success: boolean;
  shouldRefresh?: boolean;
  orderNumber?: string | number | null;
  order?: (Partial<UpdateOrderResult> & { releasedCosts?: ReleasedCost[] | null }) | null;
  releasedCosts?: ReleasedCost[] | null;
}

/** Aplica el cierre del modal de eliminación sobre el modelo local. */
export function applyDeleteOrderDialogResult(
  services: any[],
  result: DeleteOrderDialogResult | null | undefined,
  fallbackService?: any
): void {
  if (!result?.success) {
    return;
  }

  clearServiceOrdersByOrderNumber(
    services,
    result.orderNumber ?? fallbackService?.order?.orderNumber,
    result.order?.orderId ?? fallbackService?.order?.orderId ?? null
  );
  applyReleasedCosts(
    services,
    result.releasedCosts ?? result.order?.releasedCosts
  );
}
